/**
 * ProductDetailPage「利用状況」タブの表示ロジック。
 *
 * component から切り出した純粋関数群。副作用なし・framework 非依存。
 * ラベルマップは shared/types から import するだけで重複定義しない。
 */
import { LOST_REASON_LABELS } from '@shared/types/sales-action';
import { VERIFICATION_STATUS_LABELS } from '@shared/types/customer-problem';
import type { ProductUsageDetail, ProductUsageEntry } from '@shared/types/product';

export type UsageSegment = 'active' | 'cases' | 'opportunity' | 'lost';

/** 現在のセグメントに対応する行配列を返す。 */
export function usageRows(
  detail: ProductUsageDetail | null,
  seg: UsageSegment,
): ProductUsageEntry[] {
  if (!detail) return [];
  if (isActiveUsageSegment(seg)) return detail.active;
  return seg === 'opportunity' ? detail.in_progress : detail.lost;
}

/** active / cases は同じテーブル構造 (starts_on / ends_on 列) を使う。 */
export function isActiveUsageSegment(seg: UsageSegment): boolean {
  return seg === 'active' || seg === 'cases';
}

/** 商談名列は opportunity / lost セグメントのみ表示する。 */
export function showOpportunityColumn(seg: UsageSegment): boolean {
  return seg === 'opportunity' || seg === 'lost';
}

/** 行ゼロ時のメッセージ。 */
export function usageEmptyMessage(seg: UsageSegment): string {
  return seg === 'cases' ? '事例がありません' : '該当する事務所がありません';
}

/** 失注行の失注元ラベル。 */
export function lostSourceLabel(row: ProductUsageEntry): string {
  if (row.source === 'sales_action') return '営業アクション';
  if (row.opportunity_name) return `商談: ${row.opportunity_name}`;
  return '商談';
}

/** 失注理由のラベル。未設定は '-'。 */
export function lostReasonLabel(value: ProductUsageEntry['lost_reason']): string {
  if (!value) return '-';
  return LOST_REASON_LABELS[value] ?? value;
}

/** 確認状態のラベル。未設定は '-'。 */
export function verificationStatusLabel(
  value: ProductUsageEntry['verification_status'],
): string {
  if (!value) return '-';
  return VERIFICATION_STATUS_LABELS[value] ?? value;
}

/** 配列を '、' 区切りの文字列に変換。空は '-'。 */
export function listLabel(values: string[]): string {
  return values.length > 0 ? values.join('、') : '-';
}

/** AI スコアの表示。null は '-'。 */
export function scoreLabel(value: number | null): string {
  return value === null ? '-' : `${value}`;
}

/** 失注日は completed_at → updated_at → ends_on の優先順。 */
export function lostDate(row: ProductUsageEntry): string | null {
  return row.completed_at ?? row.updated_at ?? row.ends_on;
}

/** Decimal 文字列を日本円表記に整形。不正値はそのまま返す。 */
export function formatPrice(value: string | null): string {
  if (!value) return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return `${n.toLocaleString('ja-JP')}円`;
}
